import React, { useState, useEffect } from 'react'
import { X, CreditCard, CheckCircle, AlertCircle, Loader2 } from 'lucide-react'
import { MembershipPlan, ProcessPaymentResponse } from '@/types'
import { processPayPalPayment } from '@/services/paymentService'
import { useTranslation } from '@/i18n'

interface PayPalPaymentDialogProps {
  isOpen: boolean
  onClose: () => void
  plan: MembershipPlan
  billingCycle: 1 | 12 // 1=月付, 12=年付
  onSuccess?: (message: string) => void
  onError?: (error: string) => void
}

const PayPalPaymentDialog: React.FC<PayPalPaymentDialogProps> = ({
  isOpen,
  onClose,
  plan,
  billingCycle,
  onSuccess,
  onError
}) => {
  const { t } = useTranslation()
  const [isLoading, setIsLoading] = useState(false)
  const [paymentStatus, setPaymentStatus] = useState<'idle' | 'processing' | 'success' | 'error'>('idle')
  // const [paymentUrl, setPaymentUrl] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  // 重置状态
  useEffect(() => {
    if (isOpen) {
      setPaymentStatus('idle')
      // setPaymentUrl(null)
      setError(null)
    }
  }, [isOpen])


  const handlePayment = async () => {
    try {
      setIsLoading(true)
      setPaymentStatus('processing')
      setError(null)

      // 调用PayPal支付API，自动区分新订阅和升级订阅
      let paymentResult: ProcessPaymentResponse;

      // 严格判断：
      // 1. 如果plan.isNewSubscription === true → 新订阅
      // 2. 否则，如果plan.isNewSubscription === false且plan.upgradeFrom有效 → 升级订阅
      // 3. 否则 → 默认新订阅
      const isUpgrade =
        (plan?.isNewSubscription === false) &&
        plan?.upgradeFrom != null &&
        plan?.upgradeFrom !== 0; // 确保upgradeFrom是有效的订阅类型值

      // 使用统一的支付处理函数，传递所有必要的参数
      paymentResult = await processPayPalPayment(
        isUpgrade,
        plan.id,
        billingCycle,
        plan.upgradeFrom, // 当前订阅类型，仅升级时需要
        plan.upgradePrice  // 升级价格，仅升级时需要
      );

      if (paymentResult.status === 2 && paymentResult.payment_url) {
        // 支付处理中，跳转到PayPal页面
        console.log('🔗 [PayPalPaymentDialog] Opening PayPal URL:', paymentResult.payment_url)

        // 在新窗口中打开PayPal支付页面
        const popup = window.open(
          paymentResult.payment_url,
          'paypal_payment',
          'width=800,height=600,scrollbars=yes,resizable=yes'
        )

        if (popup) {
          console.log('✅ [PayPalPaymentDialog] Popup opened successfully')
          // 监听弹窗关闭
          const checkClosed = setInterval(() => {
            if (popup.closed) {
              clearInterval(checkClosed)
              console.log('🔚 [PayPalPaymentDialog] Popup closed by user')
              // 用户关闭了弹窗，重置状态
              setPaymentStatus('idle')
              setIsLoading(false)

              // 提示用户支付页面已关闭
              onSuccess?.(t('toast.membership.popupClosedRefreshing'))

              // 延迟2秒后刷新当前页面
              setTimeout(() => {
                window.location.reload()
              }, 2000)
            }
          }, 1000)

          // 立即告诉用户已跳转成功
          setIsLoading(false)
          setPaymentStatus('success')
          onSuccess?.(t('toast.membership.redirectedToPayPal'))
        } else {
          console.log('⚠️ [PayPalPaymentDialog] Popup blocked, opening in new tab')
          // 如果弹窗被阻止，直接在新标签页打开
          window.open(paymentResult.payment_url, '_blank')
          setPaymentStatus('idle')
          setIsLoading(false)
          onSuccess?.(t('toast.membership.redirectedToPayPalNewTab'))
        }
      } else {
        console.error('❌ [PayPalPaymentDialog] Invalid payment response:', paymentResult)
        throw new Error(paymentResult.message || 'Payment processing failed')
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Payment failed'
      setError(errorMessage)
      setPaymentStatus('error')
      onError?.(errorMessage)
      setIsLoading(false)
    }
  }

  const handleClose = () => {
    if (paymentStatus === 'processing') {
      // 如果正在处理中，不允许关闭
      return
    }
    onClose()
  }

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose()
    }
  }

  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4 relative">
        {/* 关闭按钮 */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
          disabled={paymentStatus === 'processing'}
        >
          <X size={24} />
        </button>

        {/* 内容区域 */}
        <div className="text-center mb-6">
          <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
            <CreditCard className="text-blue-600" size={32} />
          </div>

          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {t('payment.paypal.subscribeTo')} {plan.name}
          </h2>

          <p className="text-gray-600 mb-4">
            {plan.description}
          </p>

          <div className="text-3xl font-bold text-gray-900 mb-6">
            ¥{plan.price}/{billingCycle === 1 ? t('membership.monthly') : t('membership.yearly')}
          </div>

          {/* 功能列表 */}
          <div className="text-left mb-6">
            {plan.features.map((feature, index) => (
              <div key={index} className="flex items-center mb-2">
                <CheckCircle className="text-green-500 mr-2 flex-shrink-0" size={16} />
                <span className="text-sm text-gray-700">{feature}</span>
              </div>
            ))}
          </div>
        </div>

        {/* 错误信息 */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start">
            <AlertCircle className="text-red-500 mr-2 flex-shrink-0 mt-0.5" size={16} />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* 状态显示 */}
        {paymentStatus === 'processing' && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-center">
            <Loader2 className="text-blue-500 animate-spin mr-2" size={16} />
            <span className="text-sm text-blue-700">{t('payment.paypal.processingPayment')}</span>
          </div>
        )}

        {paymentStatus === 'success' && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center justify-center">
            <CheckCircle className="text-green-500 mr-2" size={16} />
            <span className="text-sm text-green-700">{t('payment.paypal.paymentInProgress')}</span>
          </div>
        )}

        {/* PayPal Logo */}
        <div className="mb-6 text-center">
          <div className="inline-flex items-center justify-center p-2 bg-gray-50 rounded-lg">
            <div className="text-xs text-gray-600 font-medium">PayPal</div>
          </div>
          <p className="text-xs text-gray-500 mt-2">{t('payment.paypal.secureFastPayment')}</p>
        </div>

        {/* 支付按钮 */}
        <button
          onClick={handlePayment}
          disabled={isLoading || paymentStatus === 'processing'}
          className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
        >
          {isLoading ? (
            <>
              <Loader2 className="animate-spin mr-2" size={16} />
              {t('payment.paypal.processing')}
            </>
          ) : (
            t('payment.paypal.payWithPayPal')
          )}
        </button>

        {/* 底部说明 */}
        <div className="mt-4 text-xs text-gray-500 text-center">
          {t('payment.paypal.afterPaymentRedirect')}
        </div>
      </div>
    </div>
  )
}

export default PayPalPaymentDialog